// @flow
import * as React from 'react';
import styled from 'styled-components';
import _ from 'lodash';

import type { TRound } from '../../modules/types';

import Fixture from '../../components/Fixture';
import { getTeamProjectedScore } from '../../helpers/team';
import {
	getFinalsRounds,
	getLeagueFinalsRoundsGamesCount,
	isSecondaryFinals,
	getLastRoundBeforeFinals,
} from '../../helpers/league';
import colors from '../../assets/css/colors';

import { isRoundComplete } from '../../helpers/rounds';
import ByeRoundMessage from './ByeRoundMessage';
import FinalRoundNotDeterminedMessage from './FinalRoundNotDeterminedMessage';

const FixtureStyled = styled(Fixture)`
	margin-bottom: ${({ compact }) => compact ? `1px` : `20px`};
`;

const FixturesWrapper = styled.div`
	margin-top: 20px;
	margin-bottom: ${({ is_first }) => is_first ? '0' : '50px'};
	${({ compact }) => compact && `box-shadow: ${colors.box_shadow}`};
`;

type Props = {
	ladder_teams_by_id: Object,
	league: Object,
	rounds_by_id: Object,
	rounds: Array<TRound>,
	has_assistant_coach: boolean,
	coach_players_by_id: Object,
	selected_round_id: ?number,
	is_league_bye_round: boolean,
	league_current_round_fixtures: Array<any>,
	compact?: boolean,
	players: Object,
	show_finals_trophy?: boolean,
	is_league_overview_page: boolean,
}

class Fixtures extends React.Component<Props> {
	static defaultProps = {
		is_league_overview_page: false,
	}
	get finals_rounds() {
		const { league, rounds } = this.props;
		return getFinalsRounds(league, rounds);
	}

	get is_finals_round() {
		const { selected_round_id } = this.props;
		return this.finals_rounds.includes(selected_round_id);
	}

	get gf_round_id() {
		const { league } = this.props;
		return _.get(league, 'end_round');
	}

	get is_gf_round() {
		const { selected_round_id } = this.props;
		return this.gf_round_id === selected_round_id;
	}

	get is_gf_complete() {
		const { rounds } = this.props;
		const final_round = _.get(rounds, this.gf_round_id);
		return final_round ? isRoundComplete(final_round) : false;
	}

	get has_fixtures() {
		const { league_current_round_fixtures } = this.props;
		return _.size(league_current_round_fixtures) > 0;
	}

	get show_finals_not_determined_message() {
		return this.is_finals_round && !this.has_fixtures;
	}

	get show_trophy() {
		const { show_finals_trophy } = this.props;
		return show_finals_trophy && this.is_gf_complete && this.is_gf_round;
	}

	get is_show_final_ladder_rank() {
		const {
			league,
			rounds_by_id,
			compact,
		} = this.props;

		const last_round_before_finals_id = getLastRoundBeforeFinals(league);
		const last_round_before_finals = _.get(rounds_by_id, last_round_before_finals_id);

		return this.is_finals_round
			&& compact
			&& last_round_before_finals
			&& isRoundComplete(last_round_before_finals);
	}

	renderTeamsFixture(team_ids: [number, number]) {
		const {
			ladder_teams_by_id,
			league,
			rounds,
			has_assistant_coach,
			coach_players_by_id,
			selected_round_id,
			compact,
			players
		} = this.props;

		const home_team = ladder_teams_by_id[team_ids[0]] || {};
		const away_team = ladder_teams_by_id[team_ids[1]] || {};
		const round = rounds.find((r: TRound) => r.id === selected_round_id);

		if(!round) {
			return null;
		}

		const show_final_ladder_rank = this.is_show_final_ladder_rank;

		const home_points = _.get(home_team, `scoreflow[${selected_round_id || ''}]`, null);
		const away_points = _.get(away_team, `scoreflow[${selected_round_id || ''}]`, null);

		const home_lineup_project_score =
			getTeamProjectedScore(home_team.lineup, players, coach_players_by_id, league, round);
		const away_lineup_project_score =
			getTeamProjectedScore(away_team.lineup, players, coach_players_by_id, league, round);

		const fixture = {
			home: {
				id: home_team.id,
				name: home_team.name,
				firstname: home_team.firstname,
				lastname: home_team.lastname,
				points: home_points,
				projected_points: home_lineup_project_score,
				lineup: home_team.lineup,
				rank: home_team.rank,
				user_id: home_team.user_id,
				avatar_version: home_team.avatar_version
			},
			away: {
				id: away_team.id,
				name: away_team.name,
				firstname: away_team.firstname,
				lastname: away_team.lastname,
				points: away_points,
				projected_points: away_lineup_project_score,
				lineup: away_team.lineup,
				rank: away_team.rank,
				user_id: away_team.user_id,
				avatar_version: away_team.avatar_version
			}
		};

		return (
			<FixtureStyled
				key={team_ids.toString()}
				compact={compact}
				shadow={!compact}
				fixture={fixture}
				league={league}
				round={round}
				has_assistant_coach={has_assistant_coach}
				players={players}
				show_final_ladder_rank={show_final_ladder_rank}
				show_trophy={this.show_trophy}
			/>
		);
	}

	renderFinalsWithSecondary() {
		const {
			league,
			is_league_bye_round,
			league_current_round_fixtures,
			compact,
			selected_round_id,
			rounds,
			is_league_overview_page,
		} = this.props;

		const league_rounds_final_games_count = getLeagueFinalsRoundsGamesCount(league, rounds);
		const round_final_games_count = _.get(league_rounds_final_games_count, selected_round_id);

		const primary_finals =
			league_current_round_fixtures.slice(0, round_final_games_count);
		const secondary_finals =
			league_current_round_fixtures.slice(round_final_games_count);

		return (
			<React.Fragment>
				<FixturesWrapper compact={compact} is_first>
					{is_league_bye_round 
						? <ByeRoundMessage 
							league_id={league.id} 
							is_league_overview_page={is_league_overview_page} 
						/> 
						: null
					}

					{this.show_finals_not_determined_message
						? <FinalRoundNotDeterminedMessage league_id={league.id} />
						: null
					}

					{!is_league_bye_round ?
						primary_finals.map((fixture_team_ids, index) =>
							this.renderTeamsFixture(fixture_team_ids)
						) : null
					}
				</FixturesWrapper>
				<FixturesWrapper compact={compact}>
					{!is_league_bye_round ?
						secondary_finals.map((fixture_team_ids, index) =>
							this.renderTeamsFixture(fixture_team_ids)
						) : null
					}
				</FixturesWrapper>
			</React.Fragment>
		);
	}

	renderByeRoundMessage(){
		const {
			is_league_bye_round,
			is_league_overview_page,
			league
		} = this.props;

		return is_league_bye_round
			? <ByeRoundMessage 
				league_id={league.id} 
				is_league_overview_page={is_league_overview_page}
			/> 
			: null;
	}
	renderFinalsMessage(){
		const { league } = this.props;
		return this.show_finals_not_determined_message
			? <FinalRoundNotDeterminedMessage league_id={league.id} />
			: null;
	}
	renderFixtures(){
		const { 
			is_league_bye_round, 
			league_current_round_fixtures 
		} = this.props;

		return !is_league_bye_round 
			? league_current_round_fixtures.map((fixture_team_ids, index) =>
				this.renderTeamsFixture(fixture_team_ids)
			) : null;
	}

	render() {
		const { league, compact } = this.props;

		if (this.is_finals_round && compact && isSecondaryFinals(league)) {
			return this.renderFinalsWithSecondary();
		}

		return (
			<FixturesWrapper compact={compact}>
				{ this.renderByeRoundMessage() }
				{ this.renderFinalsMessage() }
				{ this.renderFixtures() }
			</FixturesWrapper>
		);
	}
}

export default Fixtures;


// WEBPACK FOOTER //
// ./src/components/Fixtures/index.js